import axios from 'axios';
import { useCallback } from 'react';

// 返却される候補住所の型
export type AddressCandidateType = {
  provinceCode: string;
  city: string;
  address1: string;
  prefName: string;
};

// searchZipcodeの返り値型
export type SearchZipcodeResultType = {
  zipcode: string;
  candidates: AddressCandidateType[];
};

export const useSearchZipcode = () => {
  const searchZipcode = useCallback(async (zipcode: string): Promise<SearchZipcodeResultType> => {
    // 異常検知は関数呼び出し前に確認する
    const formattedZip = validateAndFormatZip(zipcode) as string;

    try {
      const apiResults = await searchZipCloud(formattedZip);

      if (apiResults && apiResults.length > 0) {
        const candidates: AddressCandidateType[] = apiResults.map((item) => ({
          provinceCode: 'JP-' + item.prefcode.padStart(2, '0'),
          city: item.address2,
          address1: item.address3,
          prefName: item.address1,
        }));

        return { zipcode: formattedZip, candidates: candidates };
      } else {
        return { zipcode: formattedZip, candidates: [] };
      }
    } catch (error) {
      return { zipcode: formattedZip, candidates: [] };
    }
  }, []);

  return { searchZipcode };
};

type ZipCloudApiResponseType = {
  message: string | null;
  results: ZipCloudApiResultType[] | null;
  status: number;
};

type ZipCloudApiResultType = {
  zipcode: string;
  prefcode: string;
  address1: string;
  address2: string;
  address3: string;
  kana1: string;
  kana2: string;
  kana3: string;
};

export const searchZipCloud = async (zipcode: string): Promise<ZipCloudApiResultType[] | null> => {
  const response = await axios.get<ZipCloudApiResponseType>(
    'https://zipcloud.ibsnet.co.jp/api/search',
    {
      params: { zipcode },
    }
  );
  return response.data.status === 200 && response.data.results ? response.data.results : null;
};

/**
 * 郵便番号の形式をバリデーションし、ハイフンを除いた7桁の文字列を返す。
 * 許容形式:
 *   - 1234567
 *   - 123-4567
 *
 * @param zipcode - ユーザーが入力した郵便番号文字列
 * @returns 正常: ハイフン除去済みの7桁文字列 / 異常: null
 */
export const validateAndFormatZip = (zipcode: string): string | null => {
  if (!zipcode) return null;

  // 全角数字や全角ハイフンが入っている可能性を考慮して半角に変換
  const normalized = normalizeZip(zipcode).trim();

  // 郵便番号形式: 「7桁の数字」または「3桁-4桁」
  const zipPattern = /^(\d{3})-?(\d{4})$/;
  const match = normalized.match(zipPattern);

  if (!match) return null;

  // グループ化した数字を連結して「1234567」の形式に統一
  return `${match[1]}${match[2]}`;
};

/**
 * 入力された郵便番号を全角→半角に正規化する
 */
const normalizeZip = (zipcode: string): string => {
  return zipcode
    .trim()
    .replace(/[０-９]/g, (char) => String.fromCharCode(char.charCodeAt(0) - 0xfee0))
    .replace(/[ー−―‐]/g, '-');
};
